import type { AccommodationQuantityType } from "@@/shared/acquire/accommodation-quantity-types";
import type {
    TranslatableValue} from "@@/shared/translations/translation";
import {
    translation,
} from "@@/shared/translations/translation";
import type { Branded } from "@@/utilities/branded";
import { minMaxRangeZodSchema } from "@@/utilities/set-within-range";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import objectHash from "object-hash";
import type { Except, IsEqual } from "type-fest";
import type { ZodType} from "zod";
import { z } from "zod";

type CapacityHash = Branded<string, "CapacityHash">;
const capacityHashZodString = z.string() as unknown as ZodType<CapacityHash>;
const capacityHashFactory = (params: FactoryParams): CapacityHash =>
    objectHash(params) as CapacityHash;

const capacityZodObject = z.object({
    ADULT: minMaxRangeZodSchema.nullish(),
    CHILD: minMaxRangeZodSchema.nullish(),
    INFANT: minMaxRangeZodSchema.nullish(),
    ANIMAL: minMaxRangeZodSchema.nullish(),
    GUEST: minMaxRangeZodSchema.nullish(),
    hash: capacityHashZodString.optional(),
});

type Capacity = z.infer<typeof capacityZodObject>;
type CapacityWithEnforcedHash = Capacity & { hash: CapacityHash };

// make sure we've got all accommodation quantity types
// as keys in capacity, and nothing more
assertTypeMatch<
    IsEqual<AccommodationQuantityType, keyof Except<Capacity, "hash">>
>();

const nullCapacity: Except<Capacity, "hash"> = {
    ADULT: null,
    CHILD: null,
    INFANT: null,
    ANIMAL: null,
    GUEST: null,
};

// make sure we've got all accommodation quantity types
// as keys in nullCapacity, no undefined, only null
assertTypeMatch<
    IsEqual<AccommodationQuantityType, keyof typeof nullCapacity>
>();

const capacityDescriptions: Map<AccommodationQuantityType, TranslatableValue> =
    new Map([
        [
            "GUEST",
            translation({
                sv: "Gäster sätter gränser även för totala antalet av vuxna och barn. Exempel: Sätts antal vuxna till 1-10, barn 0-2 och gäster till 1-5. Går det inte boka fler än 5 vuxna, eller 3 vuxna och 2 barn, totalt 5.",
                en: "Guests sets limits for the total number of adults and children. Example: If the number of adults is set to 1-10, children 0-2 and guests to 1-5. It is not possible to book more than 5 adults, or 3 adults and 2 children, a total of 5.",
            }),
        ],
    ]);

const _capacityFactoryParamsZodObject = capacityZodObject.omit({
    hash: true,
});
type FactoryParams = z.infer<typeof _capacityFactoryParamsZodObject>;
const capacityFactory = (params: FactoryParams): Capacity => {
    return {
        ...params,
        hash: capacityHashFactory(params),
    };
};

export {
    capacityDescriptions,
    capacityFactory,
    capacityHashFactory,
    capacityHashZodString,
    capacityZodObject,
    nullCapacity,
};
export type { Capacity, CapacityHash, CapacityWithEnforcedHash };
